import { Link } from "wouter";
import { Building2, ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo-head";

const COOKIES_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Cookie Policy",
    "description": "The Proper Tool cookie policy: what cookies we use, why we use them, and how to control them.",
    "url": "https://thepropertool.com/cookies",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Cookie Policy",
        "item": "https://thepropertool.com/cookies",
      },
    ],
  },
];

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Cookie Policy"
        description="The Proper Tool cookie policy: what cookies we use, why we use them, and how to control them."
        canonical="/cookies"
        schema={COOKIES_SCHEMA}
      />
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold">The Proper Tool</span>
            </div>
          </Link>
          <Link href="/">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Cookie Policy</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: March 2026</p>

        <div className="prose prose-sm max-w-none space-y-8 text-foreground">

          <section>
            <h2 className="text-xl font-semibold mb-3">1. What Are Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work correctly, improve performance, and provide information to the site owner.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              This policy explains what cookies The Proper Tool uses, why we use them, and how you can control them.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Cookies We Use</h2>

            <div className="mt-4 space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left px-4 py-3 font-medium text-foreground">Cookie</th>
                      <th className="text-left px-4 py-3 font-medium text-foreground">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-foreground">Purpose</th>
                      <th className="text-left px-4 py-3 font-medium text-foreground">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">connect.sid</td>
                      <td className="px-4 py-3 text-muted-foreground">Essential</td>
                      <td className="px-4 py-3 text-muted-foreground">Keeps you logged in during your session</td>
                      <td className="px-4 py-3 text-muted-foreground">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">theme</td>
                      <td className="px-4 py-3 text-muted-foreground">Preference</td>
                      <td className="px-4 py-3 text-muted-foreground">Remembers your light/dark mode setting</td>
                      <td className="px-4 py-3 text-muted-foreground">Persistent (local storage)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Essential Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              Essential cookies are required for the platform to function. They enable core features such as keeping you logged in and securing your session. Without these cookies, the service cannot operate. You cannot disable essential cookies while using The Proper Tool.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Preference Cookies and Local Storage</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use browser local storage (not cookies) to remember your preferences, such as your display theme (light or dark mode). This data stays on your device and is never sent to our servers. You can clear local storage at any time through your browser settings without affecting your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. What We Do Not Use</h2>
            <p className="text-muted-foreground leading-relaxed">We do not use:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5 text-muted-foreground">
              <li>Advertising or tracking cookies</li>
              <li>Third-party analytics cookies (such as Google Analytics)</li>
              <li>Social media tracking pixels</li>
              <li>Behavioural profiling tools</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Third-Party Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              When you complete a payment via Stripe, Stripe may set its own cookies on their payment pages. These are governed by Stripe's own cookie policy and privacy policy, which you can find at stripe.com. We have no control over Stripe's cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. How to Control Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              You can control and delete cookies through your browser settings. Please note that disabling essential cookies will prevent you from logging in and using the platform.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">Links to cookie settings for common browsers:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5 text-muted-foreground">
              <li>Google Chrome: Settings &gt; Privacy and Security &gt; Cookies</li>
              <li>Safari: Preferences &gt; Privacy &gt; Manage Website Data</li>
              <li>Firefox: Settings &gt; Privacy and Security &gt; Cookies and Site Data</li>
              <li>Microsoft Edge: Settings &gt; Cookies and Site Permissions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this cookie policy from time to time. Any changes will be reflected on this page with an updated date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about our use of cookies, please contact us at hello@thepropertool.com.
            </p>
          </section>

        </div>
      </main>

      <footer className="border-t mt-16 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} The Proper Tool. All rights reserved.</span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link href="/cookies" className="hover:text-foreground transition-colors">Cookie Policy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms and Conditions</Link>
            <Link href="/referral-terms" className="hover:text-foreground transition-colors">Referral Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
